import styled from "styled-components";
import { router, Head, usePoll } from "@inertiajs/react";

import { theme } from "../_kit/theme";
import { PHYSICAL_STATUS_META } from "../_kit/status";
import StatusBadge from "../_kit/StatusBadge";
import PageShell from "../_kit/PageShell";
import { useT } from "../_kit/i18n";

// Column order: the work queue first (needs cleaning), then occupied, maintenance, ready.
const COLUMN_ORDER = [
  "vacant_dirty",
  "occupied_dirty",
  "maintenance",
  "vacant_clean",
];

const Header = styled.div`
  margin-bottom: 1rem;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.4rem;
`;

const Subtitle = styled.p`
  margin: 0.25rem 0 0;
  color: ${theme.muted};
  font-size: 0.9rem;
`;

const Board = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 0.9rem;
  align-items: start;
`;

const Column = styled.section`
  background: #ffffff;
  border: 1px solid ${theme.border};
  border-radius: 12px;
  padding: 0.75rem;
`;

const ColumnHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.6rem;
`;

const Count = styled.span`
  font-size: 0.8rem;
  font-weight: 700;
  color: ${theme.muted};
`;

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Card = styled.div`
  border: 1px solid ${theme.border};
  border-radius: 10px;
  padding: 0.6rem;
`;

const CardTop = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
`;

const Code = styled.span`
  font-weight: 700;
`;

const Loc = styled.span`
  font-size: 0.75rem;
  color: ${theme.muted};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-top: 0.5rem;
`;

const MiniBtn = styled.button`
  padding: 0.25rem 0.5rem;
  border: 1px solid ${theme.border};
  border-radius: 7px;
  background: #ffffff;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${theme.ink};
  cursor: pointer;
  &:hover {
    background: ${theme.goldSoft};
    border-color: ${theme.gold};
  }
`;

const Empty = styled.div`
  padding: 1rem;
  text-align: center;
  color: ${theme.muted};
  font-size: 0.8rem;
`;

const SETTABLE = [
  { status: "vacant_clean", key: "mark_clean" },
  { status: "vacant_dirty", key: "mark_dirty" },
  { status: "maintenance", key: "mark_maintenance" },
];

const HousekeepingIndex = (props) => {
  const { t } = useT();
  const rooms = props.rooms || [];

  // Keep the board honest without manual refresh (background partial reload).
  usePoll(45_000);

  const setStatus = (room, status) => {
    router.post(
      `/frontdesk_v2/rooms/${room.id}/physical_status`,
      { status, return_to: "/frontdesk_v2/housekeeping", date: props.date },
      { preserveScroll: true },
    );
  };

  const byStatus = {};
  COLUMN_ORDER.forEach((status) => {
    byStatus[status] = [];
  });
  rooms.forEach((room) => {
    (byStatus[room.status] || (byStatus[room.status] = [])).push(room);
  });

  return (
    <PageShell refreshMs={45_000}>
      <Head title={t("hk_title")} />
      <Header>
        <Title>{t("hk_title")}</Title>
        <Subtitle>{t("hk_subtitle")}</Subtitle>
      </Header>

      <Board>
        {COLUMN_ORDER.map((status) => {
          const meta = PHYSICAL_STATUS_META[status];
          const list = byStatus[status] || [];
          return (
            <Column key={status}>
              <ColumnHead>
                <StatusBadge meta={meta} />
                <Count>{list.length}</Count>
              </ColumnHead>
              <Cards>
                {list.length === 0 ? (
                  <Empty>—</Empty>
                ) : (
                  list.map((room) => (
                    <Card key={room.id}>
                      <CardTop>
                        <Code>{room.code}</Code>
                        <Loc>{room.room_type_name}</Loc>
                      </CardTop>
                      <Actions>
                        {SETTABLE.filter(
                          (option) => option.status !== room.status,
                        ).map((option) => (
                          <MiniBtn
                            key={option.status}
                            type="button"
                            onClick={() => setStatus(room, option.status)}
                          >
                            {t(option.key)}
                          </MiniBtn>
                        ))}
                      </Actions>
                    </Card>
                  ))
                )}
              </Cards>
            </Column>
          );
        })}
      </Board>
    </PageShell>
  );
};

export default HousekeepingIndex;
